<script setup lang="ts">
import type { AcademyLink } from '#academy-virtual/types'
import { Button } from '@forgd/ui-kit/button'
import { Sheet, SheetBody, SheetClose, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@forgd/ui-kit/sheet'
import { ArrowTopRightOnSquareIcon, XMarkIcon } from '@heroicons/vue/24/outline'

export interface Props {
  id?: string
  isOpen?: boolean
  path: AcademyLink
  title?: string
  snippetLoaderClass?: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  close: [_: void]
}>()
const tracer = useTracer()

const { model: appOpen } = useAppSlideover(props.id ?? (props.path as string))

// send analytics for opening the slideover
watch(() => props.isOpen, (val) => {
  if (val) {
    tracer.custom('academy_slideover:open', { path: props.path })
  }
}, {
  immediate: true,
})

const showFooter = computed(() => {
  const path = props.path as string
  // exclude links to partials
  return path.startsWith('/') && !path.includes('/_')
})

function close() {
  appOpen.value = false
  emit('close')
}

onUnmounted(() => {
  appOpen.value = false
})
</script>

<template>
  <Sheet :open="isOpen || appOpen" :modal="false" @update:open="(open) => !open && close()">
    <SheetContent
      side="right"
      size="sm"
      data-ui="Sheet"
      @interact-outside="(e) => e.preventDefault()"
    >
      <SheetHeader>
        <SheetTitle class="text-sm font-semibold">
          {{ title || 'Readings & Resources' }}
        </SheetTitle>
        <SheetClose as-child>
          <Button type="button" size="sm" variant="ghost" icon-only aria-label="Close" data-testid="close-slideover-button">
            <XMarkIcon />
          </Button>
        </SheetClose>
      </SheetHeader>

      <SheetBody>
        <div class="prose prose-sm">
          <AcademySnippetLoader :path="path" :class="snippetLoaderClass" />
        </div>
      </SheetBody>

      <SheetFooter v-if="showFooter">
        <Button as-child class="w-full items-center justify-center h-[50px] font-display">
          <NuxtLink :to="path">
            Read on Forgd Academy
            <ArrowTopRightOnSquareIcon />
          </NuxtLink>
        </Button>
      </SheetFooter>
    </SheetContent>
  </Sheet>
</template>
